import React, { useRef, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface TabsTriggerProps {
  children: React.ReactNode
  value: string
  activeTab: string
  onClick: (value: string) => void
  fullWidth?: boolean
  tabRef?: React.RefObject<HTMLButtonElement>
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({
  children,
  value,
  activeTab,
  onClick,
  fullWidth = false,
  tabRef
}) => (
  <button
    ref={tabRef}
    className={`${
      fullWidth ? 'flex-1' : 'flex-shrink-0'
    } px-6 py-2 text-xl font-medium transition-colors duration-200 rounded-md ${
      activeTab === value ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
    } flex justify-center items-center`}
    onClick={() => onClick(value)}
  >
    {children}
  </button>
)

interface TabsProps {
  tabs: { value: string; label: React.ReactNode; badge?: React.ReactNode }[]
  activeTab: string
  onChange: (value: string) => void
}

export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onChange }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const [needsScrolling, setNeedsScrolling] = useState(false)

  // Function to scroll tabs left or right
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const scrollAmount = container.clientWidth * 0.75 // Scroll 75% of visible width

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }

  // Check if scrolling is needed and if arrows should be visible
  const checkScrollingNeeded = () => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth

    setNeedsScrolling(hasHorizontalOverflow)
    setShowLeftArrow(hasHorizontalOverflow && container.scrollLeft > 0)
    setShowRightArrow(hasHorizontalOverflow && container.scrollLeft < container.scrollWidth - container.clientWidth)
  }

  // Update scrolling status when tabs change or on window resize
  useEffect(() => {
    checkScrollingNeeded()

    // Add scroll event listener
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScrollingNeeded)
    }

    // Add resize listener
    window.addEventListener('resize', checkScrollingNeeded)

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollingNeeded)
      }
      window.removeEventListener('resize', checkScrollingNeeded)
    }
  }, [tabs])

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (!scrollContainerRef.current || !needsScrolling || !activeTabRef.current) return

    const container = scrollContainerRef.current
    const activeTabElement = activeTabRef.current

    if (activeTabElement) {
      const containerRect = container.getBoundingClientRect()
      const tabRect = activeTabElement.getBoundingClientRect()

      // If tab is not fully visible in the container
      if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
        activeTabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }, [activeTab, needsScrolling])

  return (
    <div className="relative p-1 rounded-lg mb-3 bg-white">
      {/* Left navigation arrow - only visible when scrolling is needed */}
      {needsScrolling && showLeftArrow && (
        <button
          className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 rounded-full shadow-md p-1 hover:bg-gray-100"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>
      )}

      {/* Scrollable tabs container - only apply scrolling styles when needed */}
      <div
        ref={scrollContainerRef}
        className={`flex ${needsScrolling ? 'overflow-x-auto gap-1 px-7' : 'w-full'}`}
        style={
          needsScrolling
            ? {
                scrollbarWidth: 'none' /* Firefox */,
                msOverflowStyle: 'none' /* IE and Edge */,
                WebkitOverflowScrolling: 'touch'
              }
            : {}
        }
      >
        {/* Hide scrollbar in WebKit browsers */}
        {needsScrolling && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
            div::-webkit-scrollbar {
              display: none;
            }
          `
            }}
          />
        )}

        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            activeTab={activeTab}
            onClick={onChange}
            tabRef={tab.value === activeTab ? activeTabRef : undefined}
            fullWidth={!needsScrolling}
          >
            <div className="flex items-center justify-center">
              {tab.label}
              {tab.badge && <span className="ml-2">{tab.badge}</span>}
            </div>
          </TabsTrigger>
        ))}
      </div>

      {/* Right navigation arrow - only visible when scrolling is needed */}
      {needsScrolling && showRightArrow && (
        <button
          className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 rounded-full shadow-md p-1 hover:bg-gray-100"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>
      )}
    </div>
  )
}
